import { rem } from 'polished';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Box, Header as HeaderBokeh, Paragraph as ParagraphBokeh } from '@vimeo/bokeh';
import {
  ForbiddenError,
  RateLimitError,
  ResourceNotFoundError,
  UnauthorizedError,
  NotAvailableError,
} from '@vimeo/core/libs/errors';
import { translate } from '@vimeo/i18n-runtime';
import { Header, Paragraph } from '@vimeo/iris/typography';
import { type ErrorPageDisplayedProps, useErrorPageTracking, usePicoEffect } from '@vimeo/picox';
import { useAnalyticsEvent } from '@vimeo/use-tracking';

import { SearchField } from './search';

const Container = styled.div`
  margin-top: ${rem(40)};
  text-align: center;
`;

const Title = styled(Header).attrs({ size: '1' })`
  margin-top: 0;
  margin-bottom: ${rem(10)};
`;

const Content = styled.div`
  margin: 0 auto;
  max-width: ${rem(660)};
  padding: 0 ${rem(15)} ${rem(10)};
`;

const Message = styled(Paragraph).attrs({ size: '1' })`
  margin-bottom: ${rem(30)};
  display: block;
`;

const View: React.FC<{ title: string; message: string; shouldShowSearch?: boolean }> = ({
  title,
  message,
  shouldShowSearch = true,
}) => {
  // Log the error to the console once
  useEffect(() => {
    // Availability should catch this log worst case scenario
    console.error(`[Vimeo Error Page] ${title}: ${message}`);
  }, [title, message]);

  return (
    // Availability audits rely on this id
    // Do not change unless updating the Availability audits too
    <Container id="vimeo-error-page-view">
      <Title>{title}</Title>
      <Content>
        <Message>{message}</Message>
        {shouldShowSearch && <SearchField id="error-page-search" />}
      </Content>
    </Container>
  );
};

// Bokeh version of the View component. It was implemented because styled-components based one doesn't look correct in dark mode in bokeh env.
// It looks like we need to keep both versions for a while since the component is used in many apps (outside vimeo-next) which may not be migrated to bokeh yet.
const ViewBokeh: React.FC<{ title: string; message: string; shouldShowSearch?: boolean }> = ({
  title,
  message,
  shouldShowSearch = true,
}) => {
  // Log the error to the console once
  useEffect(() => {
    // Availability should catch this log worst case scenario
    console.error(`[Vimeo Error Page] ${title}: ${message}`);
  }, [title, message]);

  return (
    // Availability audits rely on this id
    // Do not change unless updating the Availability audits too
    <Box id="vimeo-error-page-view" textAlign="center" marginTop="500">
      <HeaderBokeh variant="heading-xl" as="h1" marginBottom="100">
        {title}
      </HeaderBokeh>
      <Box margin="0 auto" maxWidth={rem(660)} paddingX="200" paddingBottom="100">
        <ParagraphBokeh marginBottom="400">{message}</ParagraphBokeh>
        {shouldShowSearch && <SearchField id="error-page-search" />}
      </Box>
    </Box>
  );
};

const getErrorNameFromConstructor = (constructorFunction: Function): string => {
  switch (constructorFunction) {
    case RateLimitError:
      return 'Rate Limit Error';
    case ResourceNotFoundError:
      return 'Resource Not Found Error';
    case ForbiddenError:
      return 'Forbidden Error';
    case UnauthorizedError:
      return 'Unauthorized Error';
    case NotAvailableError:
      return 'Not Available Error';
  }
  return 'Technical Difficulties';
};

enum BPEventNames {
  VISIT_ERROR_PAGE = 'vimeo_com.visit_error_page',
}
interface VisitErrorPageFields {
  error_type: string;
  error_message: string | null;
  url: string;
}

export const visitErrorPageEvent = (fields: VisitErrorPageFields) => ({
  eventName: BPEventNames.VISIT_ERROR_PAGE,
  version: 1,
  fields,
});

export interface ErrorPageProps {
  error: Error;
  shouldShowSearch?: boolean;
  useBokeh?: boolean;
}

export function ErrorPage(props: ErrorPageProps) {
  const { error, shouldShowSearch, useBokeh = false } = props;
  const sendAnalyticsEvent = useAnalyticsEvent();
  const { trackErrorPageDisplayed } = useErrorPageTracking();
  // Calculate View component based on prop rather than `useIsBokeh()` hook
  // to avoid having this component updated without code owners knowing about that.
  const ViewComponent = useBokeh ? ViewBokeh : View;

  useEffect(
    () =>
      sendAnalyticsEvent(
        visitErrorPageEvent({
          error_type: getErrorNameFromConstructor(error.constructor),
          error_message: error.message || null,
          url: window.location.href,
        }),
      ),
    [],
  );

  const errorMapping: Pick<ErrorPageDisplayedProps, 'errorType' | 'errorPageErrorCode'> =
    error instanceof RateLimitError
      ? { errorType: 'rate_limit', errorPageErrorCode: 429 }
      : error instanceof ResourceNotFoundError
        ? { errorType: 'resource_not_found', errorPageErrorCode: 404 }
        : error instanceof ForbiddenError
          ? { errorType: 'forbidden', errorPageErrorCode: 403 }
          : error instanceof UnauthorizedError
            ? { errorType: 'unauthorized', errorPageErrorCode: 401 }
            : error instanceof NotAvailableError
              ? { errorType: 'not_available', errorPageErrorCode: 451 }
              : { errorType: 'technical_difficulties', errorPageErrorCode: null };

  usePicoEffect(
    () => {
      trackErrorPageDisplayed({ errorPageType: 'vimeo_next', ...errorMapping });
    },
    [],
    { once: true },
  );
  switch (error.constructor) {
    default:
      if (typeof window !== 'undefined') {
        window.DD_RUM &&
          window.DD_RUM.onReady(() => {
            window.DD_RUM.addError(error, { component: 'TechnicalDifficulties' });
          });
      }

      return (
        <ViewComponent
          title={translate('Technical Difficulties')}
          message={translate(
            'Whoops! We are having some technical difficulties, please try again in a minute.',
          )}
          shouldShowSearch={shouldShowSearch}
        />
      );

    case RateLimitError:
      return (
        <ViewComponent
          title={translate('Whoa there!')}
          message={translate(
            'Sorry, you have made too many requests in a short period of time. Please wait a few minutes and try again.',
          )}
          shouldShowSearch={shouldShowSearch}
        />
      );

    case ResourceNotFoundError:
      return (
        <ViewComponent
          title={translate('Sorry, we couldn’t find that page')}
          message={translate('Make sure you’ve typed the URL correctly, or try searching Vimeo.')}
          shouldShowSearch={shouldShowSearch}
        />
      );

    case ForbiddenError:
      return (
        <ViewComponent
          title={translate('Permission Denied')}
          message={translate(
            'Sorry but you do not have the proper permission to access this area of Vimeo.',
          )}
          shouldShowSearch={shouldShowSearch}
        />
      );

    case UnauthorizedError:
      return (
        <ViewComponent
          title={translate('Unauthorized')}
          message={translate('You are unauthorized for this action.')}
          shouldShowSearch={shouldShowSearch}
        />
      );
    case NotAvailableError:
      return (
        <ViewComponent
          title={translate('This page isn’t available in your region')}
          message=""
          shouldShowSearch={shouldShowSearch}
        />
      );
  }
}
